// i18next bootstrap — wires the runtime translation engine, browser-
// language detection, and the HTTP backend that lazy-loads each locale's
// JSON bundle from /locales/{lng}/{ns}.json.
//
// Architecture notes:
//
//   - Bundles live in web/public/locales/ so Vite serves them as static
//     assets. The HTTP backend pulls them on demand — the initial JS
//     bundle stays language-agnostic and doesn't bloat as locales grow.
//
//   - Supported locales (en, it, de, es, fr) match the migrations/036
//     CHECK constraint and the supportedLocales map in
//     internal/api/me_handler.go. Adding a sixth means touching all
//     three plus adding a public/locales/{lng}/ tree.
//
//   - Detection order: localStorage (explicit user choice from a prior
//     session) → navigator (browser language) → htmlTag fallback. The
//     fallback chain `lng → fallbackLng → en` means a user with
//     navigator.language='it-CH' lands on 'it' (which we have),
//     'pt-BR' falls back to 'en' (we don't ship pt yet).
//
//   - Auth bootstrap (useAuth.tsx, on session restore) reads
//     /me/preferences.locale and calls setLocale() below if the server
//     has a value that differs from the client's current language. Both
//     PATCH /me/preferences on explicit user switch (LanguageSwitcher)
//     and the auth-bootstrap sync flow keep server + client aligned;
//     localStorage is authoritative for anonymous visitors.

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import HttpBackend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import type { SupportedLocale } from './lib/api'
import type { FlagCode } from './components/Flag'

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'it', 'de', 'es', 'fr', 'pt'] as const

// LOCALE_LABELS surfaces a human-readable display name for each locale
// in its OWN language — that's the standard expectation for a language
// switcher (a French speaker scanning the menu looks for "Français",
// not "French"). Translated names live in the locale bundles themselves
// for the *current* UI language; this map is the fallback / native name
// shown regardless of which language the UI is rendered in.
export const LOCALE_NATIVE_NAMES: Record<SupportedLocale, string> = {
  en: 'English',
  it: 'Italiano',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
}

// LOCALE_FLAG_CODES maps each locale to the ISO 3166-1 alpha-2 country
// codes whose flags represent it visually. Most locales have a single
// canonical country, but a few (EN, ES, PT) are spoken by populations
// large enough that picking one country reads as a snub of the others —
// EN gets GB + US, ES gets ES + MX, PT gets PT + BR. The downstream
// FlagStack component renders one or many flags side by side based on
// this array's length, so adding a country to a locale is a one-line
// edit here.
//
// The codes themselves are consumed by the <Flag /> component in
// components/Flag.tsx — its FLAG_CODES tuple is the source of truth
// for which SVGs ship in the bundle. Adding a new country here also
// requires adding the import to Flag.tsx.
export const LOCALE_FLAG_CODES: Record<SupportedLocale, readonly FlagCode[]> = {
  en: ['GB', 'US'],
  it: ['IT'],
  de: ['DE'],
  es: ['ES', 'MX'],
  fr: ['FR'],
  pt: ['PT', 'BR'],
}

// SHIPPED_LOCALES are the locales whose translation bundles are
// production-ready and appear in the picker. Product call
// (2026-05-20): we don't render "coming soon" rows for unshipped
// locales — a new locale shows up in the picker the moment its bundle
// lands, not before. The DB-level pre-allow pattern (CHECK constraint
// listing extra codes) is still useful as a way to let translation
// work proceed without first running a migration, but the UI side
// only knows about what's actually shippable.
//
// Display order is load-bearing — this array is rendered in-order by
// LanguageSwitcher + Settings's LanguageSection. The order is:
//   1. en (default, double-flag GB+US)
//   2. es (double-flag ES+MX)
//   3. pt (double-flag PT+BR)
//   4. de (single, alphabetical-by-native-name after the doubles)
//   5. fr (single, alphabetical)
//   6. it (single, alphabetical)
// Grouping the double-flag locales at the top keeps the column-aligned
// flags visually consistent (the prior interleave with single-flag
// locales read as inconsistent in F&F feedback 2026-05-20); the
// remaining singles fall in alphabetical order so the choice is
// predictable and the order isn't quietly editorialised by the team's
// own locale priorities.
export const SHIPPED_LOCALES: readonly SupportedLocale[] = ['en', 'es', 'pt', 'de', 'fr', 'it'] as const

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],
    // nonExplicitSupportedLngs lets 'it-IT' / 'de-AT' resolve to their
    // base language ('it' / 'de') instead of falling all the way through
    // to 'en'. Critical for browser-language detection — most browsers
    // emit region-tagged values.
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    // Namespaces are loaded eagerly (not on-demand) so the first paint
    // of any page renders fully translated rather than flashing
    // English-keys-then-real-strings. Each namespace is a small JSON
    // file (<10 KB gzipped); 7 parallel fetches over HTTP/2 are
    // cheaper than the FOUC. Add a new namespace here AND drop a
    // {ns}.json into web/public/locales/{en,it,de}/.
    ns: ['common', 'auth', 'onboarding', 'dashboard', 'course', 'settings', 'stats'],
    defaultNS: 'common',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'luno_locale',
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes by default, so i18next's HTML-escaping is
      // redundant and would double-escape any literal punctuation that
      // happens to look like markup.
      escapeValue: false,
    },
    // In production a missing key falls through to English silently so
    // an in-progress translation never paints {{key}} placeholders at
    // users. In dev / test, surface missing keys loudly so they're
    // obvious when adding new strings.
    saveMissing: false,
    returnEmptyString: false,
    react: {
      useSuspense: false,
    },
  })

// setLocale is the single mutation entry point — every caller (the
// switcher, the auth-bootstrap sync, future deep-link `?lang=` handler)
// goes through here so the cache keys, language attribute, and
// localStorage stay aligned. Idempotent: calling with the current
// language is a no-op.
//
// Switching language causes every translated string on the page to
// re-render at once, which without coordination looks like a hard
// flicker. We wrap the change in the browser's View Transitions API
// (`document.startViewTransition`) — the browser snapshots the
// pre-change pixels, runs the language change, then cross-fades from
// the old snapshot to the new render automatically. ~150ms ease-out
// is the browser default and reads as "smooth handoff" rather than
// "loading state."
//
// Feature-detected: Chrome 111+ / Safari 18+ / Firefox 144+ have the
// API; older browsers + jsdom (test env) get the old immediate
// snap-render, which is the pre-2026-05-20 baseline. The fallback
// path is also the only path during SSR or when document is undefined.
export async function setLocale(locale: SupportedLocale): Promise<void> {
  if (i18n.language === locale) return
  const change = async () => {
    await i18n.changeLanguage(locale)
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }
  // Cast through unknown — view-transition support is in the DOM lib
  // typings as of TS 5.6+, but the project's @types/dom-view-transitions
  // posture is mixed, and the runtime check is what we actually care
  // about here.
  const doc = typeof document !== 'undefined' ? (document as Document & { startViewTransition?: (cb: () => unknown) => { finished: Promise<void> } }) : undefined
  if (doc?.startViewTransition) {
    await doc.startViewTransition(change).finished.catch(() => {
      // A failed view transition shouldn't strand the language
      // change — most failures are pre-existing animations interrupted
      // by ours, which is harmless. Swallow so the caller's promise
      // resolves on the same timeline as the legacy path.
    })
    return
  }
  await change()
}

export default i18n
